// Conversation bootstrap API — one request replacing the per-endpoint
// conversation-open waterfall. The response sections mirror the individual
// endpoints exactly, so a supported server collapses:
//   GET /:id, GET /:id/runs, GET /:id/messages, GET /:id/children,
//   anchors/query, navigation/capabilities, and the breadcrumb ancestor
//   walk — into a single GET /:id/bootstrap.
// An older server (independent deployments) returns 404 for the route; the
// hook treats only that status as "unsupported" and callers fall back to the
// existing per-endpoint flow. Auth/authz failures surface normally.

import type {
  ChildSessionListResponse,
  Session,
  SessionMessageListResponse,
  ThreadAnchorRef,
} from "@/lib/session";
import type { AccessTokenGetter } from "@/lib/utils";
import { fetchWithAuth } from "@/lib/utils";
import type { NavigationCapabilities } from "@/api/conversation-navigation";
import type { SessionNavigationResponse } from "@/api/conversation-navigation";
import type { ConversationViewSummary } from "@/api/conversation-views";
import type { FollowUpSuggestionsEnvelope } from "@/api/follow-up-suggestions";
import type { PromptQueueItem } from "@/hooks/use-prompt-queue";
import { recordRecentBootstrapSuccess } from "@/hooks/use-backend-connectivity";

/** Mirrors the agno-server /runs entry shape consumed by useAgnoChat. */
export interface BootstrapRun {
  session_id?: string;
  run_id: string;
  agent_id?: string | null;
  user_message?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  status: string;
  is_silent?: boolean;
  parent_run_id?: string | null;
  parent_agent_id?: string | null;
}

/** Minimal ancestor summary for the breadcrumb; metadata carries thread_anchor. */
export interface BreadcrumbAncestor {
  id: string;
  title: string;
  parent_session_id: string | null;
  session_type?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface ConversationBootstrap {
  session: Session;
  runs: BootstrapRun[];
  messages: SessionMessageListResponse;
  children: ChildSessionListResponse;
  anchors: ThreadAnchorRef[];
  navigation: NavigationCapabilities;
  follow_up_capability: number;
  breadcrumb_ancestors: BreadcrumbAncestor[];
  generated_at: string;
  views?: { views: ConversationViewSummary[] } | null;
  view_capabilities?: { browser_file_pins: number; pinned_file_editing: number } | null;
  prompt_queue?: { items: PromptQueueItem[]; supports_attachments: boolean } | null;
  follow_up_suggestions?: FollowUpSuggestionsEnvelope | null;
  navigation_window?: SessionNavigationResponse | null;
}

export interface BootstrapResult {
  data: ConversationBootstrap | null;
  /** 404 → the connected server predates the endpoint; fall back to per-endpoint flow. */
  unsupported: boolean;
  status: number;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** Structural guard so a partial/malformed 200 never poisons the feed state. */
export function isConversationBootstrap(value: unknown): value is ConversationBootstrap {
  if (!isObject(value)) return false;
  return isObject(value.session)
    && typeof value.session.id === "string"
    && isObject(value.messages)
    && Array.isArray(value.messages.messages)
    && Array.isArray(value.runs)
    && isObject(value.children)
    && Array.isArray(value.children.items)
    && Array.isArray(value.anchors)
    && isObject(value.navigation)
    && typeof value.navigation.turn_index === "boolean"
    && typeof value.navigation.target_window === "boolean"
    && typeof value.navigation.child_navigation_metadata === "boolean"
    && typeof value.follow_up_capability === "number"
    && Array.isArray(value.breadcrumb_ancestors)
    && typeof value.generated_at === "string"
    && (value.views === undefined || value.views === null || (isObject(value.views) && Array.isArray(value.views.views)))
    && (value.view_capabilities === undefined || value.view_capabilities === null || (isObject(value.view_capabilities) && typeof value.view_capabilities.browser_file_pins === "number" && typeof value.view_capabilities.pinned_file_editing === "number"))
    && (value.prompt_queue === undefined || value.prompt_queue === null || (isObject(value.prompt_queue) && Array.isArray(value.prompt_queue.items)))
    && (value.follow_up_suggestions === undefined || value.follow_up_suggestions === null || isObject(value.follow_up_suggestions))
    && (value.navigation_window === undefined || value.navigation_window === null || isObject(value.navigation_window));
}

export async function fetchConversationBootstrap(
  sessionId: string,
  getToken: AccessTokenGetter,
  signal?: AbortSignal,
  options?: { limit?: number; completeTurns?: boolean; includeNavigation?: boolean; includeChildren?: boolean; includeViews?: boolean; includeViewCapabilities?: boolean; includePromptQueue?: boolean; includeFollowUpSuggestions?: boolean; includeNavigationWindow?: boolean },
): Promise<BootstrapResult> {
  const params = new URLSearchParams();
  params.set("limit", String(options?.limit ?? 50));
  params.set("complete_turns", String(options?.completeTurns ?? true));
  params.set("include_navigation", String(options?.includeNavigation ?? true));
  params.set("include_children", String(options?.includeChildren ?? true));
  params.set("include_views", String(options?.includeViews ?? true));
  params.set("include_view_capabilities", String(options?.includeViewCapabilities ?? true));
  params.set("include_prompt_queue", String(options?.includePromptQueue ?? true));
  params.set("include_follow_up_suggestions", String(options?.includeFollowUpSuggestions ?? true));
  params.set("include_navigation_window", String(options?.includeNavigationWindow ?? true));
  const response = await fetchWithAuth(
    `/api/sessions/${encodeURIComponent(sessionId)}/bootstrap?${params}`,
    { headers: {}, signal },
    getToken,
  );
  if (response.status === 404) {
    // FastAPI's route miss is "Not Found"; the bootstrap handler's missing
    // session response is "Session not found". Never disable the endpoint for
    // every conversation because one session was deleted or inaccessible.
    let detail: unknown;
    try { detail = (await response.json() as { detail?: unknown }).detail; } catch { /* unknown 404 */ }
    return { data: null, unsupported: detail === "Not Found", status: 404 };
  }
  if (!response.ok) return { data: null, unsupported: false, status: response.status };
  try {
    const payload: unknown = await response.json();
    if (!isConversationBootstrap(payload)) {
      return { data: null, unsupported: false, status: response.status };
    }
    recordRecentBootstrapSuccess();
    return { data: payload, unsupported: false, status: response.status };
  } catch {
    return { data: null, unsupported: false, status: response.status };
  }
}
